/**
 * 复合指标仪表盘组件
 * 用于显示复合指标如PES、MCE、数据飞轮速度指数等
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { clsx } from 'clsx';

interface CompositeMetricGaugeProps {
  title: string;
  value: number;
  maxValue?: number;
  minValue?: number;
  description: string;
  components: Array<{
    name: string;
    value: number;
    weight: number;
    color: string;
  }>;
  thresholds?: {
    excellent: number;
    good: number;
    warning: number;
  };
  className?: string;
}

const CompositeMetricGauge: React.FC<CompositeMetricGaugeProps> = ({
  title,
  value,
  maxValue = 10,
  minValue = 0,
  description,
  components,
  thresholds,
  className
}) => {
  // 计算百分比
  const percentage = Math.min(Math.max((value - minValue) / (maxValue - minValue), 0), 1);
  
  // 确定健康度等级
  const getHealthLevel = () => {
    if (!thresholds) return 'good';
    
    if (value >= thresholds.excellent) return 'excellent';
    if (value >= thresholds.good) return 'good';
    if (value >= thresholds.warning) return 'warning';
    return 'critical';
  };

  const healthLevel = getHealthLevel();
  
  // 健康度颜色映射
  const healthColors = {
    excellent: '#10b981', // green-500
    good: '#3b82f6',      // blue-500
    warning: '#f59e0b',   // amber-500
    critical: '#ef4444'   // red-500
  };

  // 创建仪表盘数据
  const gaugeData = [
    { name: 'filled', value: percentage, color: healthColors[healthLevel] },
    { name: 'empty', value: 1 - percentage, color: '#e5e7eb' }
  ];

  // 组件分布数据计算逻辑已内置到display中，这里不需要额外处理

  return (
    <div className={clsx('card p-6', className)}>
      {/* 标题和描述 */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>

      <div className="flex items-center space-x-6">
        {/* 仪表盘 */}
        <div className="relative">
          <ResponsiveContainer width={120} height={120}>
            <PieChart>
              <Pie
                data={gaugeData}
                cx="50%"
                cy="50%"
                startAngle={90}
                endAngle={-270}
                innerRadius={35}
                outerRadius={55}
                paddingAngle={0}
                dataKey="value"
              >
                {gaugeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          
          {/* 中心数值 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className={clsx(
                'text-lg font-bold',
                `text-${healthLevel === 'excellent' ? 'green' : 
                       healthLevel === 'good' ? 'blue' :
                       healthLevel === 'warning' ? 'yellow' : 'red'}-600`
              )}>
                {value.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">
                /{maxValue}
              </div>
            </div>
          </div>
        </div>

        {/* 组件分解 */}
        <div className="flex-1">
          <div className="space-y-3">
            {components.map((component) => (
              <div key={component.name} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: component.color }}
                  />
                  <span className="text-sm text-gray-700">{component.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {component.value.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500">
                    权重 {component.weight}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 健康度指示 */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">健康度等级</span>
              <span className={clsx(
                'px-2 py-1 rounded-full text-xs font-medium',
                healthLevel === 'excellent' ? 'bg-green-100 text-green-800' :
                healthLevel === 'good' ? 'bg-blue-100 text-blue-800' :
                healthLevel === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              )}>
                {healthLevel === 'excellent' ? '优秀' :
                 healthLevel === 'good' ? '良好' :
                 healthLevel === 'warning' ? '警告' : '关键'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 阈值说明 */}
      {thresholds && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 space-y-1">
            <div>优秀: ≥ {thresholds.excellent}</div>
            <div>良好: ≥ {thresholds.good}</div>
            <div>警告: ≥ {thresholds.warning}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompositeMetricGauge;